import map from 'lodash/map';
import memoize from 'lodash/memoize';
import noop from 'lodash/noop';

import * as Actions from '../actions';
import { addCollectionItem, removeCollectionItem } from '../actions/Collection';
import { closeModal, openModalCoachmark, openNarrativeModal } from '../actions/Modal';
import { tireOfTrack, unThumbDownTrack, unThumbUpTrack } from '../actions/Playback';
import { deletePlaylist, deleteTrackFromPlaylist } from '../actions/Playlist';
import { openShareModal } from '../actions/Share';
import { deleteSeed } from '../actions/Station';
import * as AppConstants from '../constants';
import { messages } from '../constants/Collection';
import QATags from '../constants/QATags';
import {
  SourceCardActionsDisabled,
  SourceCardConstants,
  SourceCardItemsForNonPremium,
} from '../constants/SourceCard';
import { PandoraTypes, ThumbprintSeedId } from '../constants/SourceTypes';
import { getInDomain } from '../domains/externalUtils';
import { isMobileDevice } from '../lib/CapabilitiesUtils';
import { getAnnotationsById } from '../lib/EntityUtils';
import { hasNoRadio, isRadioOnly, isRetired, isUnavailable } from '../lib/RightsUtils';
import { isStationType } from '../lib/SourceTypeUtils';
import { getStore } from '../store/StoreAccessor';
import { type ValueOf } from '../types';

import { isDesktopDeepLinkingEnabled } from './configuration/TunerVarUtils';
import { hasPremiumFeatures } from './ListenerUtils';

interface IMenuItemData {
  isDisabled: boolean;
  name: $TSFixMe;
  onClick?: $TSFixMeFunction;
  qaTag?: ValueOf<typeof QATags>;
  subSourceCardMenuData?: $TSFixMe;
}

// Begin request for isUnavailable data and isRadioOnly track.
function testTrackPermissions(name: string, context: $TSFixMe): boolean {
  const { itemIsRadioOnly, itemIsUnavailable, itemHasNoRadio, itemIsRetired } = context;

  if (!(name in SourceCardActionsDisabled)) {
    return false;
  }
  const sourceCardPermissions =
    SourceCardActionsDisabled[name as keyof typeof SourceCardActionsDisabled];

  const trackPermissionsMap = {
    isRadioOnly: itemIsRadioOnly,
    isUnavailable: itemIsUnavailable,
    hasNoRadio: itemHasNoRadio,
    isRetired: itemIsRetired,
  };

  if (
    // check if any of the keys have a true entry in sourceCardPermssions && that the flag is true
    Object.entries(trackPermissionsMap).some(
      ([sourceCardKey, flag]) =>
        sourceCardPermissions[sourceCardKey as keyof typeof sourceCardPermissions] && flag,
    )
  ) {
    return true;
  }
  return false;
}

function isItemAllowedForTier(
  item: keyof typeof SourceCardItemsForNonPremium,
  isPremiumListener: boolean,
): boolean {
  if (!isPremiumListener && item in SourceCardItemsForNonPremium) {
    return SourceCardItemsForNonPremium[item];
  }
  return true;
}

function bindOnClick(func: $TSFixMeFunction, store: $TSFixMe, context: $TSFixMe): $TSFixMeFunction {
  return func.bind(null, store, context);
}

function addCollectionOnClick(
  store: $TSFixMe,
  {
    pandoraId,
    stationId,
    pandoraType,
    isUncollectedThumbprintRadio,
    onAddToMyMusicClick,
  }: $TSFixMe,
): void {
  // Show TPR toast when user attempts to click this option for an uncollected TPR
  if (isUncollectedThumbprintRadio) {
    store.dispatch(
      openModalCoachmark(AppConstants.Playback.PlaybackMessages.thumbprintRadioIneligible),
    );
    return;
  }
  store.dispatch(addCollectionItem({ pandoraId, pandoraType, stationId }));
  if (onAddToMyMusicClick) {
    onAddToMyMusicClick();
  }
}

function removeCollectionOnClick(
  store: $TSFixMe,
  { pandoraId, stationId, pandoraType, componentName }: $TSFixMe,
): void {
  store.dispatch(removeCollectionItem({ componentName, pandoraId, pandoraType, stationId }));
}

function addToPlaylistOnClick(store: $TSFixMe, { isDisabled, pandoraType }: $TSFixMe): void {
  if (isDisabled) {
    const message =
      pandoraType === PandoraTypes.ALBUM
        ? messages.cannotPlaylistAlbum
        : messages.cannotPlaylistSong;
    store.dispatch(openModalCoachmark(message));
  }
}

function removeFromPlaylistOnClick(store: $TSFixMe, { playlistData }: $TSFixMe): void {
  store.dispatch(
    deleteTrackFromPlaylist({
      itemId: playlistData.itemId,
      playlistId: playlistData.playlistId,
      playlistVersion: playlistData.playlistVersion,
      trackIndex: playlistData.trackIndex,
    }),
  );
}

function deleteOnClick(
  store: $TSFixMe,
  { pandoraId, stationId, stationData, podcastEpisodeData }: $TSFixMe,
): void {
  if (stationData.feedbackId) {
    if (stationData.isPositive) {
      // @ts-expect-error TS(2345) FIXME: Argument of type '{ feedbackId: any; }' is not ass... Remove this comment to see the full error message
      store.dispatch(unThumbUpTrack({ feedbackId: stationData.feedbackId }));
    } else {
      // @ts-expect-error TS(2345) FIXME: Argument of type '{ feedbackId: any; }' is not ass... Remove this comment to see the full error message
      store.dispatch(unThumbDownTrack({ feedbackId: stationData.feedbackId }));
    }
  } else if (pandoraId && podcastEpisodeData.canDelete) {
    store.dispatch(
      getInDomain(
        'playback.actions.removeEpisodesFeedback',
        noop,
      )({
        episodeIds: [pandoraId],
        pandoraId,
      }),
    );
  } else {
    store.dispatch(deleteSeed({ musicId: stationData.musicId, stationId }));
  }
}

function deletePlaylistOnClick(store: $TSFixMe, { pandoraId }: $TSFixMe): void {
  // note this is hard-coded to not redirect when displayed from My Music or Now Playing
  // ..if we ever re-insert this item back into the playlist backstage page, we have to redirect for that case
  store.dispatch(
    deletePlaylist({
      pandoraId,
      redirectToMyMusicPlaylists: false,
    }),
  );
}

function sendToDesktopLandingPage(
  store: $TSFixMe,
  { pandoraId, pandoraType: sourceType }: $TSFixMe,
): void {
  store.dispatch(
    // @ts-expect-error TS(2345) FIXME: Argument of type '{ pandoraId: any; sourceType: an... Remove this comment to see the full error message
    Actions.UseCases.generateDeepLink({
      pandoraId,
      sourceType,
      sourceLocation: AppConstants.DesktopApp.locationConstants.sourceCard,
    }),
  );
}

function startStationTrackOnClick(store: $TSFixMe, { pandoraId, componentName }: $TSFixMe): void {
  store.dispatch(
    // @ts-expect-error TS(2345) FIXME: Argument of type '{ pandoraId: any; rerouteBasedOn... Remove this comment to see the full error message
    Actions.UseCases.startStation({
      pandoraId,
      rerouteBasedOnTier: true,
      componentName,
    }),
  );
}

function startStationArtistOnClick(
  store: $TSFixMe,
  { artistPandoraId, artistMusicId, componentName }: $TSFixMe,
): void {
  store.dispatch(
    // @ts-expect-error TS(2345) FIXME: Argument of type '{ componentName: any; musicId: a... Remove this comment to see the full error message
    Actions.UseCases.startStation({
      componentName,
      musicId: artistMusicId,
      pandoraId: artistPandoraId,
      rerouteBasedOnTier: true,
    }),
  );
}

function tiredOnClick(store: $TSFixMe, { stationId, stationData }: $TSFixMe): void {
  store.dispatch(tireOfTrack(stationId, stationData.currentTrackToken));
}

function whySelectedOnClick(store: $TSFixMe, { stationData }: $TSFixMe): void {
  store.dispatch(
    openNarrativeModal(stationData.narrative, () => {
      store.dispatch(closeModal(undefined));
    }),
  );
}

function shareOnClick(
  store: $TSFixMe,
  { pandoraId, isUncollectedThumbprintRadio, shareParentSource }: $TSFixMe,
): void {
  // Show TPR toast when user attempts to share an uncollected TPR
  isUncollectedThumbprintRadio
    ? store.dispatch(
        openModalCoachmark(AppConstants.Playback.PlaybackMessages.thumbprintRadioIneligible),
      )
    : store.dispatch(openShareModal(pandoraId, shareParentSource));
}

function getMenuItem(name: $TSFixMe, context: $TSFixMe): IMenuItemData {
  const { pandoraId, stationData, isUncollectedThumbprintRadio } = context;
  const store = getStore();
  const isDisabled = testTrackPermissions(name, context);
  const menuItemData: IMenuItemData = { isDisabled, name };

  switch (name) {
    case SourceCardConstants.ADDTOCOLLECTION:
      menuItemData.onClick = bindOnClick(addCollectionOnClick, store, context);
      if (isUncollectedThumbprintRadio) {
        menuItemData.isDisabled = true;
      }
      menuItemData.qaTag = QATags.SCAC;
      break;
    case SourceCardConstants.REMOVEFROMCOLLECTION:
    case SourceCardConstants.DELETESTATION:
      menuItemData.onClick = bindOnClick(removeCollectionOnClick, store, context);
      menuItemData.qaTag = QATags.SCRC;
      break;
    case SourceCardConstants.ADDTOPLAYLIST:
      menuItemData.onClick = bindOnClick(addToPlaylistOnClick, store, {
        isDisabled,
        ...context,
      });
      menuItemData.qaTag = QATags.SCAP;
      menuItemData.subSourceCardMenuData = {
        type: SourceCardConstants.ADDTOPLAYLIST,
        pandoraId,
        qaTag: menuItemData.qaTag,
      };
      break;
    case SourceCardConstants.REMOVEFROMPLAYLIST:
      menuItemData.onClick = bindOnClick(removeFromPlaylistOnClick, store, context);
      menuItemData.qaTag = QATags.SCRP;
      break;
    case SourceCardConstants.DELETE:
      menuItemData.onClick = bindOnClick(deleteOnClick, store, context);
      break;
    case SourceCardConstants.DELETEPLAYLIST:
      menuItemData.onClick = bindOnClick(deletePlaylistOnClick, store, context);
      menuItemData.qaTag = QATags.SCDP;
      break;
    case SourceCardConstants.OPENINDESKTOPAPP:
      menuItemData.onClick = bindOnClick(sendToDesktopLandingPage, store, context);
      break;
    case SourceCardConstants.STARTSTATIONTRACK:
      menuItemData.onClick = bindOnClick(startStationTrackOnClick, store, context);
      menuItemData.qaTag = QATags.SCSST;
      break;
    case SourceCardConstants.STARTSTATIONARTIST:
      menuItemData.onClick = bindOnClick(startStationArtistOnClick, store, context);
      menuItemData.qaTag = QATags.SCSSA;
      break;
    case SourceCardConstants.TIRED:
      menuItemData.onClick = bindOnClick(tiredOnClick, store, context);
      menuItemData.qaTag = QATags.SCTOT;
      break;
    case SourceCardConstants.WHYSELECTED:
      menuItemData.onClick = bindOnClick(whySelectedOnClick, store, context);
      menuItemData.qaTag = QATags.SCWTS;
      break;
    case SourceCardConstants.SHARE:
      menuItemData.onClick = bindOnClick(shareOnClick, store, context);
      menuItemData.qaTag = QATags.SCS;
      if (isUncollectedThumbprintRadio) {
        menuItemData.isDisabled = true;
      }
      break;
    case SourceCardConstants.EDITSTATION:
      menuItemData.onClick = stationData.onEditStation;
      menuItemData.qaTag = QATags.SCES;
      menuItemData.isDisabled = !stationData.isEditable;
  }
  return menuItemData;
}

function getSourceCardMenuItemNames(
  context: $TSFixMe,
): ValueOf<typeof SourceCardConstants>[] | undefined {
  const {
    pandoraType,
    playlistData,
    stationData,
    podcastEpisodeData,
    inCollection,
    isBackstagePage,
    isStationButton,
  } = context;
  const addOrRemoveItem = inCollection
    ? SourceCardConstants.REMOVEFROMCOLLECTION
    : SourceCardConstants.ADDTOCOLLECTION;

  switch (pandoraType) {
    case PandoraTypes.SONG:
    case AppConstants.SourceTypes.DeepLinkContentTypes.TR:
      if (playlistData.playlistId && playlistData.isEditable) {
        return [
          addOrRemoveItem,
          SourceCardConstants.ADDTOPLAYLIST,
          SourceCardConstants.STARTSTATIONARTIST,
          SourceCardConstants.STARTSTATIONTRACK,
          SourceCardConstants.SHARE,
          SourceCardConstants.REMOVEFROMPLAYLIST,
          SourceCardConstants.OPENINDESKTOPAPP,
        ];
      }
      if (stationData.isOwned && stationData.currentTrackToken) {
        const options: ValueOf<typeof SourceCardConstants>[] = [
          addOrRemoveItem,
          SourceCardConstants.ADDTOPLAYLIST,
          SourceCardConstants.STARTSTATIONARTIST,
          SourceCardConstants.STARTSTATIONTRACK,
          SourceCardConstants.TIRED,
          SourceCardConstants.SHARE,
          SourceCardConstants.OPENINDESKTOPAPP,
        ];

        if (stationData.narrative) {
          options.splice(-1, 0, SourceCardConstants.WHYSELECTED);
        }

        return options;
      }
      if (stationData.isOwned && !stationData.disabled) {
        return [
          addOrRemoveItem,
          SourceCardConstants.ADDTOPLAYLIST,
          SourceCardConstants.STARTSTATIONARTIST,
          SourceCardConstants.STARTSTATIONTRACK,
          SourceCardConstants.SHARE,
          SourceCardConstants.DELETE,
          SourceCardConstants.OPENINDESKTOPAPP,
        ];
      }
      if (isBackstagePage && isStationButton) {
        return [
          SourceCardConstants.STARTSTATIONARTIST,
          SourceCardConstants.STARTSTATIONTRACK,
          SourceCardConstants.OPENINDESKTOPAPP,
        ];
      }
      if (isBackstagePage) {
        return [SourceCardConstants.ADDTOPLAYLIST, SourceCardConstants.OPENINDESKTOPAPP];
      }
      return [
        addOrRemoveItem,
        SourceCardConstants.ADDTOPLAYLIST,
        SourceCardConstants.STARTSTATIONARTIST,
        SourceCardConstants.STARTSTATIONTRACK,
        SourceCardConstants.SHARE,
        SourceCardConstants.OPENINDESKTOPAPP,
      ];
    case PandoraTypes.ALBUM:
      if (isBackstagePage) {
        return [SourceCardConstants.ADDTOPLAYLIST, SourceCardConstants.OPENINDESKTOPAPP];
      }
      return [addOrRemoveItem, SourceCardConstants.ADDTOPLAYLIST, SourceCardConstants.SHARE];
    case PandoraTypes.STATION:
    case PandoraTypes.USER_STATION:
    case PandoraTypes.GENRE:
      if (stationData.isStationDetails) {
        return [SourceCardConstants.EDITSTATION, SourceCardConstants.SHARE];
      }
      if (isBackstagePage) {
        return [SourceCardConstants.SHARE, SourceCardConstants.OPENINDESKTOPAPP];
      }
      return [addOrRemoveItem, SourceCardConstants.SHARE];
    case PandoraTypes.PLAYLIST:
      // see note under getMenuItem DELETEPLAYLIST if you edit this
      if (isBackstagePage) {
        return [SourceCardConstants.ADDTOPLAYLIST, SourceCardConstants.OPENINDESKTOPAPP];
      }
      if (playlistData.isEditable) {
        return [
          SourceCardConstants.ADDTOPLAYLIST,
          SourceCardConstants.DELETEPLAYLIST,
          SourceCardConstants.SHARE,
        ];
      }
      return [addOrRemoveItem, SourceCardConstants.ADDTOPLAYLIST, SourceCardConstants.SHARE];
    case PandoraTypes.ARTIST:
      if (stationData.isOwned && !stationData.disabled) {
        return [
          SourceCardConstants.SHARE,
          SourceCardConstants.DELETE,
          SourceCardConstants.OPENINDESKTOPAPP,
        ];
      }
      return [SourceCardConstants.SHARE];
    case PandoraTypes.PODCAST:
      return [addOrRemoveItem, SourceCardConstants.SHARE, SourceCardConstants.OPENINDESKTOPAPP];
    case PandoraTypes.EPISODE:
      if (podcastEpisodeData.canDelete) {
        return [
          addOrRemoveItem,
          SourceCardConstants.SHARE,
          SourceCardConstants.DELETE,
          SourceCardConstants.OPENINDESKTOPAPP,
        ];
      }
      return [addOrRemoveItem, SourceCardConstants.SHARE, SourceCardConstants.OPENINDESKTOPAPP];
    default:
      return;
  }
}

function shouldSkipItem(item: $TSFixMe, state: $TSFixMe, context: $TSFixMe): boolean {
  const { pandoraId, pandoraType, isPremiumListener, isStation, isSharePossible } = context;
  const isHostedPlaylist = getInDomain('catalog.utils.isHostedPlaylist', false);

  if (
    [SourceCardConstants.ADDTOCOLLECTION, SourceCardConstants.REMOVEFROMCOLLECTION].includes(
      item,
    ) &&
    !isPremiumListener &&
    !isStation
  ) {
    if ([PandoraTypes.PODCAST, PandoraTypes.EPISODE].includes(pandoraType)) {
      return false;
    }
    return true;
  }
  if (item === SourceCardConstants.SHARE && !isSharePossible) {
    return true;
  }
  if (item === SourceCardConstants.ADDTOPLAYLIST && isHostedPlaylist(state, pandoraId)) {
    return true;
  }
  if (!isItemAllowedForTier(item, isPremiumListener)) {
    return true;
  }
  if (item === SourceCardConstants.OPENINDESKTOPAPP && !isDesktopDeepLinkingEnabled(state)) {
    return true;
  }
  return false;
}

function generateKey(context: $TSFixMe): string {
  let values = [
    context.pandoraId,
    context.pandoraType,
    context.sourceId,
    context.albumId,
    context.stationId,
    context.artistPandoraId,
    context.artistMusicId,
    context.isBackstagePage,
    context.isStationButton,
    context.shareParentSource.shareParentSourceId,
    context.podcastEpisodeData.canDelete,
    context.componentName,
  ];
  values = values.concat(Object.values(context.playlistData));
  // We don't want the full narrative string in the key, just whether or not there's a narrative
  values = values.concat(
    map(context.stationData, (value, key) => (key === 'narrative' ? !!value : value)),
  );
  return values.join(',');
}

const sourceCardMenuItemsGenerator = (context: $TSFixMe) => {
  return function (): $TSFixMe {
    const state = (getStore() as $TSFixMe).getState();
    const itemAnnotations = getAnnotationsById(state, context.pandoraId);

    context.isPremiumListener = hasPremiumFeatures(state);
    context.inCollection = getInDomain('collection.utils.isPandoraIdInCollection', false)(
      state,
      context.pandoraId,
      context.albumId,
    );
    context.isStation = isStationType(context.pandoraType);
    context.itemIsUnavailable = isUnavailable(itemAnnotations);
    context.itemIsRadioOnly = isRadioOnly(itemAnnotations);
    context.itemHasNoRadio = hasNoRadio(itemAnnotations);
    context.itemIsRetired = isRetired(itemAnnotations);
    context.artistPandoraId =
      context.pandoraType === (PandoraTypes.SONG || PandoraTypes.ARTIST) && itemAnnotations
        ? itemAnnotations.get('artistId')
        : null;
    context.isThumbprintSeedId =
      itemAnnotations && itemAnnotations.get('seedId') === ThumbprintSeedId;
    context.isUncollectedThumbprintRadio =
      context.stationData.disabled && context.isThumbprintSeedId;
    if (!context.inCollection && context.stationId && context.isStation) {
      context.inCollection = getInDomain('playback.utils.isOwnStation')(state, context.stationId);
    }
    if (!context.inCollection && context.isThumbprintSeedId) {
      context.isUncollectedThumbprintRadio = true;
      context.stationData.disabled = true;
    }

    const sourceCardMenuItemNames = getSourceCardMenuItemNames(context) || [];
    const sourceCardMenuItems = sourceCardMenuItemNames
      .filter(name => {
        return !shouldSkipItem(name, state, context);
      })
      .map(name => getMenuItem(name, context));

    return sourceCardMenuItems;
  };
};

const getSourceCardIntegrationContext = (context: $TSFixMe): $TSFixMe => {
  const {
    pandoraId,
    pandoraType,
    stationId,
    shareParentSource: { shareParentSourceId, shareParentSourceType },
  } = context;
  let { sourceId, sourceType } = context;

  if (!sourceId) {
    sourceId = shareParentSourceId;
    sourceType = shareParentSourceType;
  }

  return {
    pandoraId,
    pandoraType,
    parentId: sourceId,
    parentType: sourceType,
    stationId,
  };
};

const memoizedGetSourceCardMenuItems = memoize(context => {
  return {
    integrationContext: getSourceCardIntegrationContext(context),
    menuItemsGenerator: sourceCardMenuItemsGenerator(context),
  };
}, generateKey);

export function shouldReturnNull(state: $TSFixMe, context: $TSFixMe): boolean {
  // It's unfortunate, but now that we're deferring generating SC options until the SC is opened
  // we can no longer count on getSourceCardMenuItemNames and shouldSkipItem to determine if the
  // the SC trigger should be hidden or disabled. So we need to identify places where the list
  // would be empty up front and this is the place to do it. Be careful to avoid expensive functions
  // if at all possible.

  // We don't show source cards to mobile web
  if (isMobileDevice(state)) {
    return true;
  }

  // Voice tracks have no source cards
  if (context.pandoraType === PandoraTypes.VOICE_TRACK) {
    return true;
  }
  return false;
}

export function getSourceCardMenuItems({
  state,
  pandoraId,
  pandoraType,

  // sourceId and sourceType are currently only used for integration context and
  // should only be passed in if these values are different than those in
  // shareParentSource, e.g. in NowPlayingTopInfo
  sourceId = null,

  sourceType = null,

  shareParentSource = {
    shareParentSourceId: null,
    shareParentSourceType: null,
  },

  playlistData = {
    playlistId: null,
    playlistVersion: null,
    itemId: null,
    trackIndex: null,
    isEditable: false,
  },

  albumId = null,

  // Only used for non-Catalog backed sources. i.e. station tracks on NP and session history
  artistMusicId = null,

  stationId = null,

  stationData = {
    isOwned: false,
    isPositive: false,
    feedbackId: null,
    musicId: null,
    isStationDetails: false,
    onEditStation: null,
    disabled: false,
    isEditable: false,
    currentTrackToken: null,
    narrative: null,
  },

  podcastEpisodeData = {
    canDelete: false,
  },

  isBackstagePage = false,
  isStationButton = false,
  isSharePossible = true,
  onAddToMyMusicClick,
  componentName = null,
}: $TSFixMe): null | $TSFixMe {
  const context = {
    pandoraId,
    pandoraType,
    sourceId,
    sourceType,
    shareParentSource: shareParentSource || {},
    playlistData: playlistData || {},
    albumId,
    artistMusicId,
    stationId,
    stationData: stationData || {},
    podcastEpisodeData: podcastEpisodeData || {},
    isBackstagePage,
    isStationButton,
    isSharePossible,
    onAddToMyMusicClick,
    componentName,
  };

  if (shouldReturnNull(state, context)) {
    return null;
  }

  return memoizedGetSourceCardMenuItems(context);
}
